import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Popup } from "devextreme-react/popup";
import LoadPanel from "devextreme-react/load-panel";
import { Button } from "devextreme-react/button";
import data from "../../assets/apiRoutes.json";
import { MESSAGES } from "../../common/Messages";
import "./bolPages.css";

/*
 * The attachments panel from the mockup: which bill-of-lading pages a freight bill carries, who sent them
 * and when, and a download for each (T20260930.0001).
 *
 * Both the listing and the download go through the shared WM attachment manager, because both are reading
 * the same ABDOCUMENTS row keyed by R_SOURCE - here the freight bill's. A second listing of its own would
 * be a second thing to keep right.
 */

const formatSize = (bytes) => {
    if (!bytes) return "";
    const kb = bytes / 1024;
    return kb < 1024 ? `${Math.round(kb)} KB` : `${(kb / 1024).toFixed(1)} MB`;
};

const formatSent = (value) => {
    if (!value) return "";
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? "" : parsed.toLocaleString();
};

const BolPagesPopup = ({ visible, freightBill, accessToken, onClose }) => {
    const [pages, setPages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [downloading, setDownloading] = useState(null);

    const authHeader = useCallback(
        () => ({ access_token: accessToken, Authorization: `Bearer ${accessToken}` }),
        [accessToken]
    );

    useEffect(() => {
        if (!visible || !freightBill) return;

        let cancelled = false;
        setLoading(true);
        setError("");
        setPages([]);

        axios
            .get(`${window._env_.REACT_APP_APIS}/${data.AttachmentManager.GetAttachedDocSummariesBySourceID}`, {
                params: { sourceId: freightBill.RowId },
                headers: authHeader(),
            })
            .then((response) => {
                if (cancelled) return;
                setPages(Array.isArray(response.data) ? response.data : []);
            })
            .catch(() => {
                if (!cancelled) setError(MESSAGES.BOL_PAGES_LOAD_FAILED);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        // Guards against a late response from a popup the user has already closed writing over the next one.
        return () => { cancelled = true; };
    }, [visible, freightBill, authHeader]);

    const download = useCallback(
        async (page) => {
            setDownloading(page.rowid);
            setError("");

            try {
                const response = await axios.get(
                    `${window._env_.REACT_APP_APIS}/${data.AttachmentManager.DownloadAttachedDocsByID}`,
                    { params: { ROWID: page.rowid }, headers: authHeader() }
                );

                const file = response.data;
                if (!file || !file.base64str) {
                    setError(MESSAGES.BOL_PAGE_DOWNLOAD_FAILED);
                    return;
                }

                // Built as a blob rather than handed to the browser as a data: URL - Chrome refuses to
                // navigate to a data: URL of any size, and a scanned page is comfortably past the limit.
                const binary = atob(file.base64str);
                const bytes = new Uint8Array(binary.length);
                for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);

                const url = URL.createObjectURL(
                    new Blob([bytes], { type: file.contenttype || "application/octet-stream" })
                );

                const link = document.createElement("a");
                link.href = url;
                link.download = page.filename || file.filename || "bol";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                // Freed on the next tick: revoking immediately can cancel the download the click started.
                setTimeout(() => URL.revokeObjectURL(url), 0);
            } catch {
                setError(MESSAGES.BOL_PAGE_DOWNLOAD_FAILED);
            } finally {
                setDownloading(null);
            }
        },
        [authHeader]
    );

    if (!freightBill) return null;

    return (
        <Popup
            visible={visible}
            onHiding={onClose}
            dragEnabled={true}
            hideOnOutsideClick={true}
            showCloseButton={true}
            showTitle={true}
            title={`${MESSAGES.BOL_FREIGHT_BILL} ${(freightBill.TransactionNumber || "").trim()}`}
            container=".dx-viewport"
            width="min(560px, 92vw)"
            height="auto"
        >
            <LoadPanel visible={loading} showIndicator={true} shading={false} />

            <div className="bolpages">
                <div className="bolpages__summary">
                    {[freightBill.CustomerName, freightBill.ConsigneeName]
                        .map((v) => (v || "").trim())
                        .filter(Boolean)
                        .join(" → ")}
                </div>

                <div className="bolpages__heading">{MESSAGES.BOL_ATTACHMENTS}</div>

                {!loading && pages.length === 0 && (
                    <div className="bolpages__empty">{MESSAGES.BOL_NO_ATTACHMENTS}</div>
                )}

                {pages.map((page) => (
                    <div key={page.rowid} className="bolpages__row">
                        <div className="bolpages__detail">
                            <div className="bolpages__name">{page.filename}</div>
                            <div className="bolpages__meta">
                                {[
                                    page.subcategory,
                                    page.creationuserid ? `${MESSAGES.BOL_SENT_BY} ${page.creationuserid}` : "",
                                    formatSent(page.creationdatetime),
                                    formatSize(page.size),
                                ]
                                    .filter(Boolean)
                                    .join(" · ")}
                            </div>
                        </div>
                        <Button
                            text={MESSAGES.BOL_DOWNLOAD}
                            type="default"
                            stylingMode="outlined"
                            disabled={downloading === page.rowid}
                            onClick={() => download(page)}
                        />
                    </div>
                ))}

                {error && <div className="bolpages__error">{error}</div>}
            </div>
        </Popup>
    );
};

export default BolPagesPopup;
